import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

interface DevBannerPreviewProps {
  isEditMode?: boolean;
  client?: any;
  effectiveIsMatriz?: boolean;
  onSimulateMatrizChange?: (value: boolean) => void;
}

/**
 * Banner DEV para preview de abas
 * 
 * Mostra chips com observabilidade:
 * - devTabs=ON (verde)
 * - isEditMode (true/false)
 * - effectiveIsMatriz (true/false)
 * - clientId (número)
 * 
 * Inclui toggle "Simular Matriz" para testar fluxo sem banco
 */
export default function DevBannerPreview({
  isEditMode = false,
  client,
  effectiveIsMatriz = false,
  onSimulateMatrizChange,
}: DevBannerPreviewProps) {
  const [simulateMatriz, setSimulateMatriz] = useState(false);

  // Matriz efetiva = real OR simulada
  const finalEffectiveIsMatriz = useMemo(() => {
    return effectiveIsMatriz || simulateMatriz;
  }, [effectiveIsMatriz, simulateMatriz]);

  const handleSimulateMatrizChange = (value: boolean) => {
    setSimulateMatriz(value);
    onSimulateMatrizChange?.(value);
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-indigo-500 p-4 rounded mb-4 space-y-3">
      {/* Chips de observabilidade */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Chip devTabs=ON */}
        <Badge variant="default" className="bg-green-600 hover:bg-green-700">
          ✓ devTabs=ON
        </Badge>

        {/* Chip isEditMode */}
        <Badge
          variant="outline"
          className={
            isEditMode
              ? "bg-blue-100 text-blue-800 border-blue-300"
              : "bg-gray-100 text-gray-800 border-gray-300"
          }
        >
          isEditMode: {isEditMode ? "true" : "false"}
        </Badge>

        {/* Chip effectiveIsMatriz */}
        <Badge
          variant="outline"
          className={
            finalEffectiveIsMatriz
              ? "bg-purple-100 text-purple-800 border-purple-300"
              : "bg-gray-100 text-gray-800 border-gray-300"
          }
        >
          effectiveIsMatriz: {finalEffectiveIsMatriz ? "true" : "false"}
        </Badge>

        {/* Chip clientId */}
        {client?.id && (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
            clientId: {client.id}
          </Badge>
        )}
      </div>

      {/* Toggle Simular Matriz */}
      <div className="flex items-center gap-3 pt-2 border-t border-indigo-200">
        <label className="text-sm font-medium text-indigo-900">
          🧪 Simular Matriz (sem persistência):
        </label>
        <Switch
          checked={simulateMatriz}
          onCheckedChange={handleSimulateMatrizChange}
          className="data-[state=checked]:bg-purple-600"
        />
        <span className="text-xs text-indigo-700">
          {simulateMatriz ? "Ativado" : "Desativado"}
        </span>
      </div>

      {/* Mensagens informativas */}
      {!isEditMode && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
          ⚠️ <strong>Aba Matriz e Filiais</strong> habilita após salvar o cliente (modo edição).
        </div>
      )}

      {isEditMode && finalEffectiveIsMatriz && (
        <div className="p-2 bg-green-50 border border-green-200 rounded text-sm text-green-900">
          ✓ Modo Matriz ativado. Você pode visualizar FilialListSection e criar filiais.
        </div>
      )}
    </div>
  );
}
